/**
 * Global date formatting utility for consistent date display across the application
 * All dates will be formatted as dd/mm/yyyy by default
 */

// Default application date format
export const APP_DATE_FORMAT = 'dd/mm/yyyy';

/**
 * Format a date string or Date object to dd/mm/yyyy format
 * @param {string|Date} dateInput - Date string or Date object to format
 * @param {string} format - Optional format string (default: 'dd/mm/yyyy')
 * @returns {string} Formatted date string or '-' if date is invalid
 */
export const formatDate = (dateInput, format = APP_DATE_FORMAT) => {
  if (!dateInput) return '-';
  
  try {
    const date = new Date(dateInput);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '-';
    }
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    // Return formatted date based on requested format
    switch (format.toLowerCase()) {
      case 'dd/mm/yyyy':
        return `${day}/${month}/${year}`;
      case 'mm/dd/yyyy':
        return `${month}/${day}/${year}`;
      case 'yyyy-mm-dd':
        return `${year}-${month}-${day}`;
      case 'dd-mm-yyyy':
        return `${day}-${month}-${year}`;
      default:
        return `${day}/${month}/${year}`;
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
};

/**
 * Format a date string or Date object to dd/mm/yyyy format with time
 * @param {string|Date} dateInput - Date string or Date object to format
 * @param {boolean} includeSeconds - Whether to include seconds in the time
 * @returns {string} Formatted date string with time or '-' if date is invalid
 */
export const formatDateTime = (dateInput, includeSeconds = false) => {
  if (!dateInput) return '-';
  
  try {
    const date = new Date(dateInput);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '-';
    }
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    
    const timeFormat = includeSeconds ? `${hours}:${minutes}:${seconds}` : `${hours}:${minutes}`;
    
    return `${day}/${month}/${year} ${timeFormat}`;
  } catch (error) {
    console.error('Error formatting date time:', error);
    return '-';
  }
};

/**
 * Parse a date string from dd/mm/yyyy format to a Date object
 * @param {string} dateString - Date string in dd/mm/yyyy format
 * @returns {Date|null} Date object or null if invalid
 */
export const parseDateString = (dateString) => {
  if (!dateString) return null;
  
  try {
    const [day, month, year] = dateString.split('/');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  } catch (error) {
    console.error('Error parsing date string:', error);
    return null;
  }
};

/**
 * Convert a date to ISO format (YYYY-MM-DD) for API requests
 * @param {Date|string} dateInput - Date to convert
 * @returns {string|null} ISO date string or null if invalid
 */
export const toISODateString = (dateInput) => {
  if (!dateInput) return null;
  
  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return null;
    
    return date.toISOString().split('T')[0];
  } catch (error) {
    console.error('Error converting to ISO date:', error);
    return null;
  }
};

/**
 * Get a date picker format object for MUI DatePicker components
 * This configures the DatePicker to display and accept dates in dd/mm/yyyy format
 */
export const getDatePickerFormat = () => {
  return {
    format: 'dd/MM/yyyy',
    mask: '__/__/____',
    placeholder: 'DD/MM/YYYY'
  };
};

/**
 * Create a date adapter for MUI DatePicker that uses dd/mm/yyyy format
 * @param {object} DateAdapter - The date adapter to use (e.g., AdapterDateFns)
 * @returns {object} Configured date adapter
 */
export const createDateAdapter = (DateAdapter) => {
  return class CustomDateAdapter extends DateAdapter {
    format(date, formatString) {
      if (formatString === 'keyboardDate') {
        return formatDate(date);
      }
      return super.format(date, formatString);
    }
    
    parse(value, formatString) {
      if (formatString === 'keyboardDate' && typeof value === 'string') {
        return parseDateString(value);
      }
      return super.parse(value, formatString);
    }
  };
};
